import { RowNode } from 'ag-grid-community';
import { setFastAllSelection } from 'components/shared/data/DataGrid/utils/serverSideModelHelpers';

export const findDetailsGrid = (node) => {
  // In some UI flows (e.g. filters/model updates) AG Grid may give us RowNodes
  // before the detail grid is fully initialized. Avoid crashing on transient states.
  // Prefer row.detailGridInfo when available - it doesn't depend on gridApi being attached.
  if (node?.detailGridInfo?.api) {
    return node.detailGridInfo;
  }

  if (!node?.gridApi?.getDetailGridInfo) return undefined;
  return node.gridApi.getDetailGridInfo(`detail_${node.id}`);
};

export const getSelectedRowsForMasterNodes = <T, D>(nodes: RowNode[], detailsMapping: (row: T) => D[]) => {
  const selectedRows: D[] = [];

  nodes.forEach((node) => {
    const detailsGrid = findDetailsGrid(node);

    if (detailsGrid?.api?.getSelectedRows) {
      // if grid exists take the selection from it
      selectedRows.push(...detailsGrid.api.getSelectedRows());
    } else {
      // if it doesn't exists add all (user never modified anything)
      selectedRows.push(...detailsMapping(node.data));
    }
  });

  return selectedRows;
};

export const updateMasterSelectionFromDetails = async (node) => {
  if (!node.detailNode) {
    return;
  }

  if (!node.detailGridInfo?.api?.getSelectedNodes) {
    return;
  }

  const selectedNodes = node.detailGridInfo.api.getSelectedNodes?.() ?? [];
  const hasSelected = selectedNodes.length > 0;
  node.setSelected(hasSelected);
};

export const updateDetailsSelectionFromMaster = (node) => {
  const isSelected = node.isSelected();
  const detailsGrid = findDetailsGrid(node);

  const apply = (detailsGridToUse) => {
    if (!detailsGridToUse?.api?.selectAll || !detailsGridToUse.api?.deselectAll) {
      return;
    }

    const selectedNodes = detailsGridToUse.api.getSelectedNodes?.() ?? [];

    if (isSelected && selectedNodes.length === 0) {
      detailsGridToUse.api.selectAll();
    }
    if (!isSelected) {
      detailsGridToUse.api.deselectAll();
    }
  };

  if (!detailsGrid) {
    // Retry once: on the first click, detail grids may not exist yet.
    setTimeout(() => {
      const detailsGridRetry = findDetailsGrid(node);
      apply(detailsGridRetry);
    }, 0);
    return;
  }

  apply(detailsGrid);
};

export const deselectMasterAndDetails = (api) => {
  setFastAllSelection(api, false);
  api.forEachDetailGridInfo(function (detailGridApi) {
    setFastAllSelection(detailGridApi.api, false);
  });
};
